import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2, AlertCircle, FileText, RotateCcw, ChevronLeft, ChevronRight, Image, Info, X, Eye, Globe } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || 'http://localhost:8000';

const STORAGE_KEY = 'knowledge-assistant-chat';

// Component to display YouTube videos
function YouTubeVideo({ videoId, url }) {
  if (!videoId) {
    console.log('YouTubeVideo: No videoId provided');
    return null;
  }
  
  // Ensure videoId is clean (no extra characters)
  const cleanVideoId = videoId.trim();
  const embedUrl = `https://www.youtube.com/embed/${cleanVideoId}?rel=0`;
  
  console.log('YouTubeVideo: Rendering video with ID:', cleanVideoId, 'URL:', embedUrl);
  
  return (
    <div className="youtube-video-container" style={{
      marginTop: '12px',
      marginBottom: '12px',
      position: 'relative',
      paddingBottom: '56.25%', // 16:9 aspect ratio
      height: 0,
      overflow: 'hidden',
      borderRadius: '8px',
      backgroundColor: '#000',
      width: '60%',
      minWidth: '400px',
      maxWidth: '800px'
    }}>
      <iframe
        src={embedUrl}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: '8px'
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        title="YouTube video"
        frameBorder="0"
      />
    </div>
  );
}

function KnowledgeFreshness({ isQueryActiveRef }) {
  const { getToken } = useAuth();
  const [freshness, setFreshness] = useState(null);
  
  useEffect(() => {
    let isMounted = true;
    let abortController = null;
    let isFetching = false; // Prevent duplicate calls
    
      const fetchFreshness = async (retryCount = 0) => {
      // Prevent duplicate calls
      if (isFetching) return;
      
      // Skip if query is active - don't interfere with user queries
      if (isQueryActiveRef && isQueryActiveRef.current) {
        return;
      }
      
      isFetching = true;
      
      // Cancel previous request if still in flight
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();
      
      try {
        // Check again before getting token - query might have started
        if (isQueryActiveRef && isQueryActiveRef.current) {
          isFetching = false;
          return;
        }
        
        // Use a longer delay to allow query requests to go first (increased from 100ms to 500ms)
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check one more time
        if (isQueryActiveRef && isQueryActiveRef.current) {
          isFetching = false;
          return;
        }
        
        // Background processes should NOT call getToken() when query is active
        // Use static API key if available, otherwise skip to avoid queuing
        const STATIC_API_KEY = process.env.REACT_APP_STATIC_API_KEY || process.env.VITE_STATIC_API_KEY;
        let token = null;
        
        // Note: KnowledgeFreshness doesn't have access to tokenCacheRef, so we'll skip if query is active
        if (isQueryActiveRef && isQueryActiveRef.current) {
          isFetching = false;
          return;
        }
        
        if (STATIC_API_KEY) {
          // Use static API key - no queuing, instant authentication
          token = STATIC_API_KEY;
        } else {
          // Only call getToken() if no static key available
          token = await getToken();
        }
        if (!token) {
          isFetching = false;
          return;
        }
        
        // CRITICAL: Check one final time before making fetch request
        // If query became active while we were getting token, abort immediately
        if (isQueryActiveRef && isQueryActiveRef.current) {
          isFetching = false;
          return;
        }
        if (abortController.signal.aborted) {
          isFetching = false;
          return;
        }
        
        const response = await fetch(`${API_URL}/api/knowledge-freshness`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: abortController.signal,
        });
        
        // Check if aborted
        if (abortController.signal.aborted || !isMounted) {
          isFetching = false;
          return;
        }
        
        // Handle 401 - try refreshing token once
        if (response.status === 401 && retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
          isFetching = false;
          if (!abortController.signal.aborted && isMounted) {
            return fetchFreshness(1);
          }
          return;
        }
        
        if (response.status === 401) {
          isFetching = false;
          return; // Skip if still 401 after retry
        }
        
        if (response.ok && isMounted) {
          const data = await response.json();
          setFreshness(data);
        }
      } catch (err) {
        // Ignore abort errors
        if (err.name !== 'AbortError') {
          // Silently fail - don't block UI
        }
      } finally {
        isFetching = false;
      }
    };
    
    // OPTIMIZATION: Delay initial fetch significantly to not block query submission
    // Wait 60 seconds before first check - this ensures queries can be submitted immediately
    // without waiting for freshness check to complete
    const initialFetchDelay = setTimeout(() => {
      if (isMounted && (!isQueryActiveRef || !isQueryActiveRef.current)) {
        fetchFreshness();
      }
    }, 60000); // Increased to 60s to prioritize queries
    
    // Then poll every 5 minutes (increased from 2 minutes), but only when query is not active
    const interval = setInterval(() => {
      if (isMounted && !isFetching && (!isQueryActiveRef || !isQueryActiveRef.current)) {
        fetchFreshness();
      }
    }, 300000); // 5 minutes (increased from 2 minutes)
    
    return () => {
      isMounted = false;
      clearTimeout(initialFetchDelay);
      if (abortController) {
        abortController.abort();
      }
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);
  
  // Show loading state or the freshness info
  if (!freshness) {
    return null; // Don't show anything while loading
  }
  
  if (!freshness.last_updated_ago) {
    return null; // Don't show if no data
  }
  
  return (
    <p className="chat-status">
      Knowledge last updated: {freshness.last_updated_ago}
    </p>
  );
}

function ImageCarousel({ sources, isVisible, onToggle, getToken }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState({});
  
  // Load images as blobs when carousel becomes visible
  useEffect(() => {
    if (!isVisible || !getToken || !sources || sources.length === 0) return;
    
    const loadImages = async () => {
      const imgUrls = {};
      
      for (const source of sources) {
        // Only load images, not PDFs
        if (!source.image_url) continue;
        
        // Check if we already have this image loaded
        setImageUrls(prev => {
          if (prev[source.image_url]) {
            imgUrls[source.image_url] = prev[source.image_url];
            return prev;
          }
          return prev;
        });
        
        // Skip if already in urls
        if (imgUrls[source.image_url]) continue;
        
        try {
          const token = await getToken();
          if (!token) continue;
          
          const fullUrl = source.image_url.startsWith('http') 
            ? source.image_url 
            : `${API_URL}${source.image_url}`;
          
          const response = await fetch(fullUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (response.ok) {
            const blob = await response.blob();
            imgUrls[source.image_url] = URL.createObjectURL(blob);
          }
        } catch (err) {
          console.error('Failed to load image:', err);
        }
      }
      
      // Only update if we have new URLs
      if (Object.keys(imgUrls).length > 0) {
        setImageUrls(prev => ({ ...prev, ...imgUrls }));
      }
    };
    
    loadImages();
    
    // Cleanup object URLs when component unmounts
    return () => {
      setImageUrls(prev => {
        Object.values(prev).forEach(url => {
          if (url && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
        return {};
      });
    };
  }, [isVisible, sources, getToken]);
  
  if (!sources || sources.length === 0) return null;
  
  const currentSource = sources[currentIndex];
  const hasMultiple = sources.length > 1;
  
  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? sources.length - 1 : prev - 1));
  };
  
  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === sources.length - 1 ? 0 : prev + 1));
  };
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };
  
  const currentImageUrl = currentSource.image_url 
    ? (imageUrls[currentSource.image_url] || `${API_URL}${currentSource.image_url}`)
    : null;
  
  const mediaType = 'image';
  
  return (
    <div className="message-images-container">
      <button 
        className="message-images-toggle"
        onClick={onToggle}
        type="button"
        aria-label={isVisible ? "Hide previews" : "Show previews"}
        title={isVisible ? "Hide previews" : `Show ${sources.length} ${mediaType}${sources.length !== 1 ? 's' : ''}`}
      >
        <Image style={{ width: '16px', height: '16px' }} />
        <span>{sources.length} {mediaType}{sources.length !== 1 ? 's' : ''}</span>
      </button>
      
      {isVisible && currentImageUrl && (
        <div className="message-image-carousel">
          <div className="carousel-container">
            {hasMultiple && (
              <button 
                className="carousel-nav carousel-nav-prev" 
                onClick={goToPrevious}
                type="button"
                aria-label="Previous image"
              >
                <ChevronLeft style={{ width: '16px', height: '16px' }} />
              </button>
            )}
            <div className="carousel-image-wrapper" style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: '100%'
            }}>
              {currentImageUrl ? (
                // Image preview
                <img 
                  src={currentImageUrl}
                  alt={`Diagram from ${currentSource.file_name}`}
                  className="message-image"
                  loading="lazy"
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '400px', 
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                />
              ) : null}
              <div className="message-image-caption" style={{ marginTop: '8px', textAlign: 'center' }}>
                {currentSource.file_name} {currentSource.page && `(Page ${currentSource.page})`}
                {hasMultiple && (
                  <span className="carousel-counter" style={{ marginLeft: '8px' }}>
                    {currentIndex + 1} / {sources.length}
                  </span>
                )}
              </div>
            </div>
            {hasMultiple && (
              <button 
                className="carousel-nav carousel-nav-next" 
                onClick={goToNext}
                type="button"
                aria-label="Next image"
              >
                <ChevronRight style={{ width: '16px', height: '16px' }} />
              </button>
            )}
          </div>
          {hasMultiple && sources.length > 1 && (
            <div className="carousel-dots">
              {sources.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  type="button"
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SourceImage({ imageUrl, fileName, getToken }) {
  const [blobUrl, setBlobUrl] = useState(null);
  
  useEffect(() => {
    if (!imageUrl || !getToken) return;
    
    let currentBlobUrl = null;
    
    const loadImage = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        
        const fullUrl = imageUrl.startsWith('http') 
          ? imageUrl 
          : `${API_URL}${imageUrl}`;
        
        const response = await fetch(fullUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          currentBlobUrl = url;
          setBlobUrl(url);
        }
      } catch (err) {
        console.error('Failed to load source image:', err);
      }
    };
    
    loadImage();
    
    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [imageUrl, getToken]);
  
  if (!blobUrl) return null;
  
  return (
    <div className="source-image-container">
      <img 
        src={blobUrl}
        alt={`Diagram from ${fileName}`}
        className="source-image"
        loading="lazy"
      />
    </div>
  );
}

function DocumentPreviewModal({ documentUrl, fileName, page, isOpen, onClose, getToken }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Determine file type from file name
  const getFileType = (filename) => {
    if (!filename || typeof filename !== 'string') return 'unknown';
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || 'unknown';
  };
  
  const fileType = getFileType(fileName || '');
  const isPDF = fileType === 'pdf';
  const isOfficeDoc = ['docx', 'pptx', 'doc', 'ppt'].includes(fileType);
  
  useEffect(() => {
    if (!isOpen || !documentUrl || !getToken) return;
    
    let currentBlobUrl = null;
    
    const loadDocument = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = await getToken();
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }
        
        const fullUrl = documentUrl.startsWith('http') 
          ? documentUrl 
          : `${API_URL}${documentUrl}`;
        
        const response = await fetch(fullUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          currentBlobUrl = url;
          setBlobUrl(url);
        } else {
          setError(`Failed to load document: ${response.statusText}`);
        }
      } catch (err) {
        console.error('Failed to load document:', err);
        setError('Failed to load document. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadDocument();
    
    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [isOpen, documentUrl, getToken]);
  
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  
  // Handle download for non-PDF files
  const handleDownload = () => {
    if (blobUrl) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '1200px',
          height: '90%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e5e5e5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText style={{ width: '20px', height: '20px', color: '#0066cc' }} />
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#333' }}>
                {fileName}
              </h3>
              {page && (
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                  Page {page}
                </p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isOfficeDoc && blobUrl && !loading && !error && (
              <button
                onClick={handleDownload}
                style={{
                  background: '#0066cc',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0052a3';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#0066cc';
                }}
              >
                <FileText style={{ width: '16px', height: '16px' }} />
                Download
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <X style={{ width: '20px', height: '20px', color: '#666' }} />
            </button>
          </div>
        </div>
        
        {/* Document Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}>
          {loading && (
            <div style={{ textAlign: 'center' }}>
              <Loader2 style={{ width: '32px', height: '32px', animation: 'spin 0.8s linear infinite', color: '#0066cc' }} />
              <p style={{ marginTop: '12px', color: '#666' }}>Loading document...</p>
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', color: '#d32f2f' }}>
              <AlertCircle style={{ width: '32px', height: '32px', margin: '0 auto 12px' }} />
              <p>{error}</p>
            </div>
          )}
          {blobUrl && !loading && !error && isPDF && (
            <iframe
              src={`${blobUrl}#page=${page || 1}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: 'white'
              }}
              title={`PDF: ${fileName}`}
            />
          )}
          {blobUrl && !loading && !error && isOfficeDoc && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              maxWidth: '500px'
            }}>
              <FileText style={{ width: '64px', height: '64px', color: '#0066cc', margin: '0 auto 20px' }} />
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
                Office Document Preview
              </h3>
              <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                This document ({fileType.toUpperCase()}) cannot be previewed in the browser. 
                Please download it to view the full content.
              </p>
              <button
                onClick={handleDownload}
                style={{
                  background: '#0066cc',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0052a3';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#0066cc';
                }}
              >
                <FileText style={{ width: '18px', height: '18px' }} />
                Download {fileName}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Ask() {
  const { getToken } = useAuth();
  const [question, setQuestion] = useState('');
  const [technicalityLevel, setTechnicalityLevel] = useState(() => {
    // Load from localStorage or default to 3
    try {
      const saved = localStorage.getItem('ask-technicality-level');
      return saved ? parseInt(saved, 10) : 3;
    } catch {
      return 3;
    }
  });
  const [messages, setMessages] = useState(() => {
    // Load messages from localStorage on mount
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert timestamp strings back to Date objects
        return parsed.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (err) {
      console.error('Failed to load chat from localStorage:', err);
    }
    return [];
  });
  const [currentSources, setCurrentSources] = useState(() => {
    // Load sources from localStorage
    try {
      const saved = localStorage.getItem(`${STORAGE_KEY}-sources`);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Failed to load sources from localStorage:', err);
    }
    return [];
  });
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [error, setError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [imagesVisible, setImagesVisible] = useState({}); // Track image visibility per message
  const [documentPreview, setDocumentPreview] = useState({ isOpen: false, documentUrl: null, fileName: null, page: null });
  const [webSearchLoading, setWebSearchLoading] = useState({}); // Track web search loading per message
  const [webSearchResults, setWebSearchResults] = useState({}); // Store web search results per message
  const [webSearchStage, setWebSearchStage] = useState({}); // Track web search loading stage per message
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Set sourcesVisible when currentSources are loaded from localStorage
 // Run when sources are loaded

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (err) {
      console.error('Failed to save chat to localStorage:', err);
    }
  }, [messages]);

  // Save technicality level to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('ask-technicality-level', technicalityLevel.toString());
    } catch (err) {
      console.error('Failed to save technicality level:', err);
    }
  }, [technicalityLevel]);

  // Save sources to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(`${STORAGE_KEY}-sources`, JSON.stringify(currentSources));
    } catch (err) {
      console.error('Failed to save sources to localStorage:', err);
    }
  }, [currentSources]);

  const exampleQuestions = [
    'What are the fire retardant properties of KoolShade?',
    'What is the startup procedure of the weaving machine?',
    'How long do I need to bake the powder coating once applied?',
    'What is the minimum louvre margin on the screen assembly brackets?',
  ];

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const consecutiveFailuresRef = useRef(0);
  const intervalIdRef = useRef(null);
  const isQueryActiveRef = useRef(false); // Track if a query is currently active
  const pollingAbortControllerRef = useRef(null); // Store abort controller to cancel in-flight polling
  const queryAbortControllerRef = useRef(null); // Store abort controller to cancel in-flight query requests
  const isMountedRef = useRef(true); // Track if component is mounted
  const startPollingRef = useRef(null); // Store function to start polling
  const lastQueryTimeRef = useRef(null); // Track when last query was made to prevent polling interference
  
  // Token cache to avoid queuing getToken() calls
  const tokenCacheRef = useRef({ token: null, expiresAt: 0 });

  useEffect(() => {
    isMountedRef.current = true;
    let isMounted = true;
    
    const checkDocuments = async () => {
      // CRITICAL: Skip if query is active - don't interfere with user queries at all
      // Document ingestion runs independently on backend, so status checks can wait
      // We must check this FIRST before any async operations
      if (isQueryActiveRef.current) {
        // Abort any in-flight request immediately
        if (pollingAbortControllerRef.current) {
          pollingAbortControllerRef.current.abort();
          pollingAbortControllerRef.current = null;
        }
        return;
      }
      
      // Skip if there was a query in the last 2 minutes - avoid interfering with user activity
      // Document ingestion runs independently on backend, so we can wait longer
      const timeSinceLastQuery = lastQueryTimeRef.current 
        ? Date.now() - lastQueryTimeRef.current 
        : Infinity;
      if (timeSinceLastQuery < 120000) { // 2 minutes - give queries plenty of time
        return;
      }
      
      // Skip if we've had too many consecutive failures (likely auth issues)
      if (consecutiveFailuresRef.current >= 3) {
        // Stop polling if auth keeps failing
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        return;
      }
      
      // Cancel previous request if still in flight
      if (pollingAbortControllerRef.current) {
        pollingAbortControllerRef.current.abort();
      }
      pollingAbortControllerRef.current = new AbortController();
      const currentAbortController = pollingAbortControllerRef.current;
      
      try {
        // Check again before getting token - query might have started
        if (isQueryActiveRef.current || currentAbortController.signal.aborted) {
          pollingAbortControllerRef.current = null;
          return;
        }
        
        // Check if there was a recent query (2 minutes threshold)
        const recentQueryTime = lastQueryTimeRef.current 
          ? Date.now() - lastQueryTimeRef.current 
          : Infinity;
        if (recentQueryTime < 120000) { // 2 minutes - give queries plenty of time
          pollingAbortControllerRef.current = null;
          return;
        }
        
        // Longer delay to ensure queries have priority - document status can wait
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
        
        // Check one more time after delay
        if (isQueryActiveRef.current || currentAbortController.signal.aborted) {
          pollingAbortControllerRef.current = null;
          return;
        }
        
        // Check again for recent query after delay (2 minutes threshold)
        const recentQueryTimeAfterDelay = lastQueryTimeRef.current 
          ? Date.now() - lastQueryTimeRef.current 
          : Infinity;
        if (recentQueryTimeAfterDelay < 120000) {
          pollingAbortControllerRef.current = null;
          return;
        }
        
        // CRITICAL: Background processes should NEVER call getToken() when query is active
        // This prevents queuing issues - they should just skip entirely
        if (isQueryActiveRef.current) {
          pollingAbortControllerRef.current = null;
          return;
        }
        
        // Use static API key if available, otherwise use cached token to avoid queuing getToken() calls
        const STATIC_API_KEY = process.env.REACT_APP_STATIC_API_KEY || process.env.VITE_STATIC_API_KEY;
        let token = null;
        
        if (STATIC_API_KEY) {
          // Use static API key - no queuing, instant authentication
          token = STATIC_API_KEY;
        } else {
          // Fall back to cached token or Clerk token
          const now = Date.now();
          const cachedToken = tokenCacheRef.current;
          
          if (cachedToken.token && cachedToken.expiresAt > (now + 300000)) {
            // Use cached token - no need to call getToken()
            token = cachedToken.token;
          } else {
            // Only get token if query is not active (triple check before calling getToken)
            if (isQueryActiveRef.current) {
              pollingAbortControllerRef.current = null;
              return;
            }
            
            // Final check - if query became active during delay, skip
            if (isQueryActiveRef.current) {
              pollingAbortControllerRef.current = null;
              return;
            }
            
            // Only now call getToken() - but this should rarely happen if caching works
            token = await getToken();
            
            // Update cache if we got a token
            if (token) {
              try {
                const parts = token.split('.');
                if (parts.length === 3) {
                  const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
                  const exp = payload.exp * 1000;
                  tokenCacheRef.current = { token: token, expiresAt: exp };
                } else {
                  tokenCacheRef.current = { token: token, expiresAt: now + 3600000 };
                }
              } catch (e) {
                tokenCacheRef.current = { token: token, expiresAt: now + 3600000 };
              }
            }
          }
        }
        
        if (!token) {
          consecutiveFailuresRef.current++;
          pollingAbortControllerRef.current = null;
          return;
        }
        
        // CRITICAL: Check one final time before making fetch request
        // If query became active while we were getting token, abort immediately
        if (isQueryActiveRef.current || currentAbortController.signal.aborted) {
          pollingAbortControllerRef.current = null;
          return;
        }
        
        // OPTIMIZATION: Only check first page (limit=1) to see if any are processing
        // This is much faster than loading 100 documents
        const response = await fetch(`${API_URL}/api/documents?limit=1&offset=0`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: currentAbortController.signal, // Make cancellable
        });
        
        // Check if aborted
        if (currentAbortController.signal.aborted || !isMounted) {
          pollingAbortControllerRef.current = null;
          return;
        }
        
        // Handle 401 - don't retry in polling, just skip this check
        if (response.status === 401) {
          consecutiveFailuresRef.current++;
          pollingAbortControllerRef.current = null;
          return; // Skip this poll, will try again next interval
        }
        
        // Reset failure count on success
        consecutiveFailuresRef.current = 0;
        
        if (response.ok && isMounted) {
          const data = await response.json();
          // Handle both old format (array) and new format (object with documents array)
          const docs = Array.isArray(data) ? data : (data.documents || []);
          const hasProcessing = docs.some(doc => doc.status === 'processing');
          
          // Stop polling if no documents are processing
          if (!hasProcessing && intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
        }
        pollingAbortControllerRef.current = null;
      } catch (err) {
        // Ignore abort errors
        if (err.name === 'AbortError') {
          pollingAbortControllerRef.current = null;
          return;
        }
        consecutiveFailuresRef.current++;
        pollingAbortControllerRef.current = null;
        // Silently fail - don't block UI
      }
    };
    
    // Function to start/restart polling interval
    const startPolling = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      if (isMounted && !isQueryActiveRef.current && consecutiveFailuresRef.current < 3) {
        intervalIdRef.current = setInterval(() => {
          if (isMounted && !isQueryActiveRef.current) {
            checkDocuments();
          }
        }, 120000); // 2 minutes (increased from 30 seconds to reduce interference)
      }
    };
    
    // Store startPolling function in a ref so it can be accessed from handleSubmit
    startPollingRef.current = startPolling;
    
    // OPTIMIZATION: Delay initial check significantly to not block page load/query submission
    // Wait 60 seconds before first check - this ensures queries can be submitted immediately
    // without waiting for document polling to complete
    const initialCheckDelay = setTimeout(() => {
      if (isMounted && consecutiveFailuresRef.current < 3 && !isQueryActiveRef.current) {
        checkDocuments();
      }
    }, 60000); // Increased to 60s to prioritize queries
    
    // Only start polling if we haven't had too many failures
    if (consecutiveFailuresRef.current < 3) {
      startPolling();
    }
    
    return () => {
      isMounted = false;
      isMountedRef.current = false;
      clearTimeout(initialCheckDelay);
      if (pollingAbortControllerRef.current) {
        pollingAbortControllerRef.current.abort();
        pollingAbortControllerRef.current = null;
      }
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      // Cancel any in-flight query when component unmounts
      if (queryAbortControllerRef.current) {
        queryAbortControllerRef.current.abort();
        queryAbortControllerRef.current = null;
      }
    };
  }, [getToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    
    // Note: Removed blocking check for documentsProcessing
    // Since uploads now process in background, queries can proceed independently
    // The knowledge base will use whatever documents are currently indexed

    const originalQuestion = question.trim();
    let currentQuestion = originalQuestion;
    const questionId = Date.now();
    
    // Check for /video trigger - simple proof of concept
    const hasVideoTrigger = originalQuestion.toLowerCase().startsWith('/video');
    
    // If /video is detected, show a demo video
    let videoData = null;
    if (hasVideoTrigger) {
      // Use a demo video ID for proof of concept
      // You can change this to any YouTube video ID
      const demoVideoId = 'jHyT8Af3uy4'; // Demo video ID
      videoData = { 
        videoId: demoVideoId, 
        url: `https://www.youtube.com/watch?v=${demoVideoId}`,
        fullUrl: `https://www.youtube.com/watch?v=${demoVideoId}`
      };
      // For /video, we'll skip the API call and just show the video
      // Don't modify currentQuestion - we'll return early before API call
    }
    
    // Add user message immediately (non-blocking)
    // Don't include videoData in user message - only show video in assistant response
    setMessages((prev) => [
      ...prev,
      {
        id: questionId,
        role: 'user',
        text: originalQuestion, // Use original question text for display
        timestamp: new Date(),
      },
    ]);

    setQuestion('');
    setError('');
    setCurrentSources([]);
    // Reset textarea height after clearing
    if (textareaRef.current) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }, 0);
    }

    // If /video trigger, skip API call and just show the video
    if (hasVideoTrigger && videoData) {
      console.log('Video trigger detected, videoData:', videoData);
      // Add a simple assistant response to show the video immediately
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          text: 'Here is a video that may support your request:',
          timestamp: new Date(),
          sources: [],
          confidence: 'high',
          confidenceReason: '',
          confidenceScore: 100,
          answerMode: 'direct_answer',
          videoData: videoData,
        },
      ]);
      return; // Exit early, don't make API call
    }

    // CRITICAL: Abort ALL background requests FIRST, before any async operations
    // This prevents browser connection queuing - background requests must be cancelled
    // before the query request is made
    if (pollingAbortControllerRef.current) {
      pollingAbortControllerRef.current.abort();
      pollingAbortControllerRef.current = null;
    }
    
    // Pause polling interval while query is active
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    
    // NOW mark query as active - after aborting background requests
    // This prevents polling from starting new requests that could block token retrieval
    isQueryActiveRef.current = true;
    lastQueryTimeRef.current = Date.now(); // Update last query time
    
    // Show loading states immediately
    setLoading(true);
    setLoadingStage('thinking');
    
    // Get token for query - use static API key if available, otherwise use Clerk token
    // Static API key avoids Clerk's getToken() queuing issues entirely
    const STATIC_API_KEY = process.env.REACT_APP_STATIC_API_KEY || process.env.VITE_STATIC_API_KEY;
    let token = null;
    
    if (STATIC_API_KEY) {
      // Use static API key - no queuing, instant authentication
      token = STATIC_API_KEY;
    } else {
      // Fall back to Clerk token with caching
      const now = Date.now();
      const cachedToken = tokenCacheRef.current;
      
      // Check if we have a valid cached token (refresh 5 minutes before expiration)
      if (cachedToken.token && cachedToken.expiresAt > (now + 300000)) {
        token = cachedToken.token;
      } else {
        // Need to get fresh token - but do it with skipCache and highest priority
        // Get token with skipCache for highest priority
        token = await getToken({ skipCache: true });
        
        // Cache the token with expiration (JWT tokens typically expire in 1 hour)
        // We'll refresh 5 minutes before expiration
        if (token) {
          try {
            // Decode JWT to get expiration (simple base64 decode)
            const parts = token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
              const exp = payload.exp * 1000; // Convert to milliseconds
              tokenCacheRef.current = {
                token: token,
                expiresAt: exp
              };
            } else {
              // Fallback: assume 1 hour expiration
              tokenCacheRef.current = {
                token: token,
                expiresAt: now + 3600000
              };
            }
          } catch (e) {
            // Fallback: assume 1 hour expiration
            tokenCacheRef.current = {
              token: token,
              expiresAt: now + 3600000
            };
          }
        }
      }
    }
    
    if (!token) {
      setError('Authentication required. Please sign in.');
      setLoading(false);
      setLoadingStage('');
      isQueryActiveRef.current = false;
      return;
    }
    
    // Make query request immediately - no setTimeout needed
    // Build conversation history from previous messages + current question
    // Explicitly include currentQuestion to ensure it's always included (no race condition with state updates)
    // Only add currentQuestion if it's not empty (video trigger will have empty question)
    const conversationHistory = [
      ...messages
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(msg => ({
          role: msg.role,
          content: msg.text
        }))
    ];
    
    // Always add the current question to conversation history
    conversationHistory.push({
      role: 'user',
      content: currentQuestion
    });
    
    // Cancel any in-flight query request
    if (queryAbortControllerRef.current) {
      queryAbortControllerRef.current.abort();
    }
    
    // Create new AbortController for this query
    const queryAbortController = new AbortController();
    queryAbortControllerRef.current = queryAbortController;
    
    // Start API call immediately (async function)
    (async () => {
      try {
        setLoadingStage('searching');

        // Token is already retrieved above with caching - use it directly
        // Check if request was cancelled
        if (queryAbortController.signal.aborted) {
          return;
        }

        // Helper function to make authenticated request with token refresh
        const makeAuthenticatedRequest = async (useStreaming = false, retryCount = 0) => {
          // Check if request was cancelled
          if (queryAbortController.signal.aborted) {
            throw new Error('Request cancelled');
          }
          
          // On retry after 401, get fresh token with skipCache to force refresh
          // Also clear cache to force new token
          if (retryCount > 0) {
            tokenCacheRef.current = { token: null, expiresAt: 0 };
            token = await getToken({ skipCache: true });
            if (!token) {
              throw new Error('Authentication required. Please sign in.');
            }
            // Update cache with new token
            const now = Date.now();
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
                const exp = payload.exp * 1000;
                tokenCacheRef.current = { token: token, expiresAt: exp };
              } else {
                tokenCacheRef.current = { token: token, expiresAt: now + 3600000 };
              }
            } catch (e) {
              tokenCacheRef.current = { token: token, expiresAt: now + 3600000 };
            }
          }
          
          // Check again if request was cancelled
          if (queryAbortController.signal.aborted) {
            throw new Error('Request cancelled');
          }
          
          // Use streaming endpoint for faster perceived response
          const endpoint = useStreaming ? `${API_URL}/api/query/stream` : `${API_URL}/api/query`;
          
          // CRITICAL: Use priority hint to ensure query requests bypass queued requests
          // This helps the browser prioritize query requests over background requests
          const fetchOptions = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ 
              question: currentQuestion, // Use currentQuestion (will be originalQuestion for normal queries)
              conversation_history: conversationHistory,
              technicality_level: technicalityLevel
            }),
            signal: queryAbortController.signal, // Make request cancellable
          };
          
          // Add priority hint if supported (Chrome/Edge) - helps browser prioritize this request
          if (typeof Request !== 'undefined' && 'priority' in Request.prototype) {
            fetchOptions.priority = 'high';
          }
          
          const response = await fetch(endpoint, fetchOptions);

          // Handle 401 - token expired, try refreshing once with longer delay
          if (response.status === 401 && retryCount === 0) {
            // Wait longer to allow Clerk to refresh token (500ms instead of 100ms)
            await new Promise(resolve => setTimeout(resolve, 500));
            // Force fresh token refresh with skipCache
            return makeAuthenticatedRequest(useStreaming, 1);
          }

          if (!response.ok) {
            // For streaming, try to read error from stream
            if (useStreaming && response.headers.get('content-type')?.includes('text/event-stream')) {
              const reader = response.body.getReader();
              const decoder = new TextDecoder();
              let errorText = '';
              try {
                const { value } = await reader.read();
                if (value) {
                  const chunk = JSON.parse(decoder.decode(value));
                  errorText = chunk.error || 'Request failed';
                }
              } catch {
                errorText = 'Request failed';
              } finally {
                reader.releaseLock();
              }
              throw new Error(errorText);
            }
            
            const data = await response.json().catch(() => ({ detail: 'Request failed' }));
            const errorMsg = data.detail || 'Query failed';
            
            // Handle rate limiting
            if (response.status === 429) {
              throw new Error(errorMsg);
            }
            
            // Handle other errors
            throw new Error(errorMsg);
          }
          
          return response;
        };
        
        // Try streaming first, fallback to non-streaming if it fails
        let useStreaming = true;
        let response;
        
        try {
          response = await makeAuthenticatedRequest(true); // Try streaming
          
          // Check if response is actually streaming
          if (!response.body || !response.headers.get('content-type')?.includes('text/event-stream')) {
            // Not a streaming response, fallback to regular
            useStreaming = false;
            throw new Error('Streaming not available');
          }
        } catch (streamError) {
          // Fallback to non-streaming endpoint
          useStreaming = false;
          response = await makeAuthenticatedRequest(false);
        }
        
        if (useStreaming) {
          // Don't set 'compiling' stage yet - wait until we start receiving chunks
          // This prevents showing "Compiling answer..." before we actually start generating
          // Handle streaming response
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let fullAnswer = '';
          let answerId = Date.now() + 1;
          let confidenceLevel = 'none';
          let confidenceReason = '';
          let confidenceScore = 0;
          let answerMode = 'direct_answer';
          let sources = [];
          let assistantMessageCreated = false; // Track if we've created the assistant message
          
          // Don't create assistant message yet - keep loading message visible
          // We'll create it when we receive the first content chunk
          
          try {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) break;
              
              // Check if request was cancelled
              if (queryAbortController.signal.aborted) {
                reader.cancel();
                return;
              }
              
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer
              
              for (const line of lines) {
                if (!line.trim()) continue;
                
                try {
                  const chunk = JSON.parse(line);
                  
                  if (chunk.type === 'chunk') {
                    // Append chunk to answer
                    fullAnswer += chunk.content || '';
                    
                    // Capture current value to avoid closure issues
                    const currentAnswer = fullAnswer;
                    
                    // Create assistant message on first chunk (replaces loading message)
                    if (!assistantMessageCreated) {
                      assistantMessageCreated = true;
                      setLoading(false); // Hide loading message
                      setLoadingStage(''); // Clear loading stage
                      // Create assistant message with first chunk
                      setMessages((prev) => [
                        ...prev,
                        {
                          id: answerId,
                          role: 'assistant',
                          text: currentAnswer,
                          timestamp: new Date(),
                          sources: [],
                          confidence: null, // Set to null initially, will be set when 'complete' chunk arrives
                          confidenceReason: '',
                          confidenceScore: 0,
                          answerMode: 'direct_answer',
                        },
                      ]);
                    } else {
                      // Update existing message with streaming text
                      // Capture answerId to avoid closure issues
                      const msgId = answerId;
                      setMessages((prev) => 
                        prev.map(msg => 
                          msg.id === msgId 
                            ? { ...msg, text: currentAnswer }
                            : msg
                        )
                      );
                    }
                  } else if (chunk.type === 'complete') {
                    // Final answer and metadata
                    fullAnswer = chunk.answer || fullAnswer;
                    confidenceLevel = chunk.confidence_level || 'none';
                    confidenceReason = chunk.reason || '';
                    confidenceScore = chunk.confidence_score || 0;
                    answerMode = chunk.answer_mode || 'direct_answer';
                    
                    // Capture all values to avoid closure issues
                    const finalAnswer = fullAnswer || "I don't have enough information to answer this question based on the available documents.";
                    const finalConfidence = confidenceLevel;
                    const finalReason = confidenceReason;
                    const finalScore = confidenceScore;
                    const finalMode = answerMode;
                    const msgId = answerId;
                    
                    // Update message with final data
                    setMessages((prev) => 
                      prev.map(msg => 
                        msg.id === msgId 
                          ? { 
                              ...msg, 
                              text: finalAnswer,
                              confidence: finalConfidence,
                              confidenceReason: finalReason,
                              confidenceScore: finalScore,
                              answerMode: finalMode
                            }
                          : msg
                      )
                    );
                  } else if (chunk.type === 'sources') {
                    // Sources received
                    sources = chunk.sources || [];
                    // Capture values to avoid closure issues
                    const currentSources = sources;
                    const msgId = answerId;
                    
                    setMessages((prev) => 
                      prev.map(msg => 
                        msg.id === msgId 
                          ? { ...msg, sources: currentSources }
                          : msg
                      )
                    );
                    setTimeout(() => {
                      setCurrentSources(currentSources);
                    }, 200);
                  } else if (chunk.type === 'error') {
                    throw new Error(chunk.error || 'Streaming error occurred');
                  }
                } catch (parseError) {
                  // Skip invalid JSON lines
                  console.warn('Failed to parse chunk:', parseError);
                }
              }
          }
          } finally {
            reader.releaseLock();
          }
        } else {
          // Non-streaming fallback
          setLoadingStage('compiling');
          const data = await response.json();
          const answerId = Date.now() + 1;
          
          setMessages((prev) => [
            ...prev,
            {
              id: answerId,
              role: 'assistant',
              text: data.answer || "I don't have enough information to answer this question based on the available documents.",
              timestamp: new Date(),
              sources: data.sources || [],
              confidence: data.confidence || 'none',
              confidenceReason: data.reason || '',
              confidenceScore: data.confidence_score || 0,
              answerMode: data.answer_mode || 'direct_answer',
            },
          ]);
          
          setTimeout(() => {
            setCurrentSources(data.sources || []);
          }, 200);
        }
      } catch (err) {
        // Don't show error if request was cancelled (user navigated away)
        if (err.name === 'AbortError' || err.message === 'Request cancelled') {
          // Silently ignore - user navigated away
          return;
        }
        setError(err.message);
      } finally {
        setLoading(false);
        setLoadingStage('');
        // Mark query as inactive to resume polling
        isQueryActiveRef.current = false;
        // Clear abort controller reference
        if (queryAbortControllerRef.current === queryAbortController) {
          queryAbortControllerRef.current = null;
        }
        
        // Resume polling after a short delay to avoid immediate interference
        setTimeout(() => {
          if (isMountedRef.current && !isQueryActiveRef.current && consecutiveFailuresRef.current < 3 && startPollingRef.current) {
            startPollingRef.current();
          }
        }, 2000); // Wait 2 seconds before resuming polling
      }
    })(); // Execute immediately (IIFE - Immediately Invoked Function Expression)
  };

  const handleExampleClick = (prompt) => {
    setQuestion(prompt);
  };

  const handleReset = () => {
    setIsResetting(true);
    setTimeout(() => {
      setMessages([]);
      setCurrentSources([]);
      setError('');
      setQuestion('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // Clear localStorage
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(`${STORAGE_KEY}-sources`);
      } catch (err) {
        console.error('Failed to clear localStorage:', err);
      }
      setIsResetting(false);
    }, 200);
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      setTimeout(() => {
        chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages, loading]);

  return (
    <div className="ask-layout">
      <div className="chat-container">
        <div className="chat-header">
          <div style={{ flex: 1 }}>
            <h1 className="chat-title">Ask</h1>
            <p className="chat-subtitle">Query your internal KoolShade AI knowledge base</p>
            <KnowledgeFreshness isQueryActiveRef={isQueryActiveRef} />
          </div>
          <div className="chat-header-right">
            <div className="reset-btn-wrapper">
              <button 
                className={`reset-btn ${isResetting ? 'resetting' : ''} ${messages.length === 0 ? 'disabled' : ''}`} 
                onClick={handleReset} 
                type="button"
                disabled={messages.length === 0}
              >
                <RotateCcw style={{ width: '14px', height: '14px' }} />
                <span>New conversation</span>
              </button>
            </div>
            
            {/* Technicality Level Slider */}
            <div className="technicality-slider-container">
              <span className="technicality-label">
                Technicality:
              </span>
              <span className="technicality-end-label simple">
                Simple
              </span>
              <div className="technicality-slider-track">
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={technicalityLevel}
                  onChange={(e) => setTechnicalityLevel(parseInt(e.target.value, 10))}
                  className="technicality-slider-input"
                  style={{
                    background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${((technicalityLevel - 1) / 4) * 100}%, var(--gray-200) ${((technicalityLevel - 1) / 4) * 100}%, var(--gray-200) 100%)`
                  }}
                  onMouseMove={(e) => {
                    // Update visual feedback
                    const slider = e.target;
                    const value = parseInt(slider.value, 10);
                    const percentage = ((value - 1) / 4) * 100;
                    slider.style.background = `linear-gradient(to right, var(--primary) 0%, var(--primary) ${percentage}%, var(--gray-200) ${percentage}%, var(--gray-200) 100%)`;
                  }}
                />
              </div>
              <span className="technicality-end-label expert">
                Expert
              </span>
              <span className="technicality-value">
                {technicalityLevel}
              </span>
            </div>
          </div>
        </div>

        <div className="chat-messages" ref={chatContainerRef}>
          {messages.length === 0 && !loading && (
            <div className="chat-empty">
              <p className="empty-text">Ask a question to get started</p>
              <p className="empty-disclaimer">
                This assistant answers only from your indexed documents. It will indicate when it doesn't have enough information to answer your question.
              </p>
              {exampleQuestions.length > 0 && (
                <div className="examples-inline">
                  {exampleQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="example-btn-inline"
                      onClick={() => handleExampleClick(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {messages.map((msg, idx) => (
            <div 
              key={msg.id} 
              className={`message ${msg.role}`}
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <div className="message-content">
                {/* Answer mode label */}
                {msg.role === 'assistant' && msg.answerMode && (
                  <div className="answer-mode-label" style={{
                    display: 'inline-block',
                    marginBottom: '8px',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    ...(msg.answerMode === 'direct_answer' ? {
                      backgroundColor: '#e6f7e6',
                      color: '#006600',
                      border: '1px solid #b3e6b3'
                    } : msg.answerMode === 'diagram_referenced_answer' ? {
                      backgroundColor: '#e6f3ff',
                      color: '#0066cc',
                      border: '1px solid #b3d9ff'
                    } : {
                      backgroundColor: '#fff4e6',
                      color: '#cc6600',
                      border: '1px solid #ffd9b3'
                    })
                  }}>
                    {msg.answerMode === 'direct_answer' && '✓ Answer from documentation'}
                    {msg.answerMode === 'diagram_referenced_answer' && '🧩 Based on technical diagrams'}
                    {msg.answerMode === 'technical_reference_only' && '📋 Technical reference (diagram)'}
                  </div>
                )}
                {msg.confidence && msg.confidence !== 'high' && msg.confidence !== null && msg.role === 'assistant' && (
                  <div className={`confidence-banner confidence-${msg.confidence}`}>
                    {msg.confidence === 'none' ? (
                      <>
                        <AlertCircle style={{ width: '14px', height: '14px' }} />
                        <span>This question isn't covered by your current documents.</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle style={{ width: '14px', height: '14px' }} />
                        <span>Partial confidence ({msg.confidenceScore}%) - {msg.confidenceReason}</span>
                      </>
                    )}
                  </div>
                )}
                <div className="message-text">{msg.text}</div>
                {/* Show YouTube video if present - ONLY for assistant messages */}
                {msg.role === 'assistant' && msg.videoData && msg.videoData.videoId && (
                  <YouTubeVideo videoId={msg.videoData.videoId} url={msg.videoData.url || msg.videoData.fullUrl} />
                )}
                {/* Show carousel ONLY if the top (most confident) source has an image */}
                {(() => {
                  if (!msg.sources || !Array.isArray(msg.sources) || msg.sources.length === 0) {
                    return null;
                  }
                  
                  // Sort sources by score (highest first) to get the top source
                  const sortedSources = [...msg.sources].sort((a, b) => (b.score || 0) - (a.score || 0));
                  const topSource = sortedSources[0];
                  
                  // Only show carousel if the top source has an image
                  if (!topSource || !topSource.image_url) {
                    return null;
                  }
                  
                  // Filter to only sources with images, deduplicate, and sort by score
                  const imageSources = msg.sources
                    .filter(s => s && s.image_url)
                    .filter((source, idx, self) => 
                      idx === self.findIndex(s => s.image_url === source.image_url)
                    )
                    .sort((a, b) => (b.score || 0) - (a.score || 0));
                  
                  if (imageSources.length === 0) {
                    return null;
                  }
                  
                  return (
                    <ImageCarousel 
                      sources={imageSources}
                      isVisible={imagesVisible[msg.id] || false}
                      onToggle={() => setImagesVisible(prev => ({
                        ...prev,
                        [msg.id]: !prev[msg.id]
                      }))}
                      getToken={getToken}
                    />
                  );
                })()}
                
                {/* Web Search Button - only show for assistant messages */}
                {msg.role === 'assistant' && (
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      data-tour="web-search-button"
                      onClick={async () => {
                        if (webSearchLoading[msg.id]) return;
                        
                        setWebSearchLoading(prev => ({ ...prev, [msg.id]: true }));
                        
                        try {
                          // Stage 1: Searching web
                          setWebSearchStage(prev => ({ ...prev, [msg.id]: 'searching' }));
                          
                          // Use skipCache to prioritize web search request over background polling
                          const token = await getToken({ skipCache: true });
                          if (!token) {
                            throw new Error('Authentication required');
                          }
                          
                          // Get the original question from conversation history
                          const messageIndex = messages.findIndex(m => m.id === msg.id);
                          const previousUserMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;
                          const searchQuery = previousUserMessage?.text || msg.text || '';
                          
                          // Stage 2: Fetching answers (while request is in flight)
                          setWebSearchStage(prev => ({ ...prev, [msg.id]: 'fetching' }));
                          
                          // Start the fetch request
                          const fetchPromise = fetch(`${API_URL}/api/web-search`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                              query: searchQuery,
                              internal_answer: msg.text,
                              internal_sources: msg.sources || [],
                            }),
                          });
                          
                          // Update stages while waiting for response
                          const stageUpdates = [
                            { stage: 'analysing', delay: 800 },
                            { stage: 'comparing', delay: 1600 },
                            { stage: 'generating', delay: 2400 },
                          ];
                          
                          const stageTimeouts = stageUpdates.map(({ stage, delay }) => 
                            setTimeout(() => {
                              setWebSearchStage(prev => {
                                if (prev[msg.id] && webSearchLoading[msg.id]) {
                                  return { ...prev, [msg.id]: stage };
                                }
                                return prev;
                              });
                            }, delay)
                          );
                          
                          // Wait for response
                          const response = await fetchPromise;
                          
                          // Handle 401 - token expired, try refreshing once
                          if (response.status === 401) {
                            // Clear stage timeouts
                            stageTimeouts.forEach(timeout => clearTimeout(timeout));
                            
                            await new Promise(resolve => setTimeout(resolve, 500));
                            const refreshedToken = await getToken({ skipCache: true });
                            if (!refreshedToken) {
                              throw new Error('Authentication required');
                            }
                            
                            // Retry with refreshed token
                            setWebSearchStage(prev => ({ ...prev, [msg.id]: 'fetching' }));
                            
                            const retryStageTimeouts = stageUpdates.map(({ stage, delay }) => 
                              setTimeout(() => {
                                setWebSearchStage(prev => {
                                  if (prev[msg.id] && webSearchLoading[msg.id]) {
                                    return { ...prev, [msg.id]: stage };
                                  }
                                  return prev;
                                });
                              }, delay)
                            );
                            
                            const retryResponse = await fetch(`${API_URL}/api/web-search`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${refreshedToken}`,
                              },
                              body: JSON.stringify({
                                query: searchQuery,
                                internal_answer: msg.text,
                                internal_sources: msg.sources || [],
                              }),
                            });
                            
                            // Clear retry stage timeouts
                            retryStageTimeouts.forEach(timeout => clearTimeout(timeout));
                            
                            if (!retryResponse.ok) {
                              throw new Error('Web search failed');
                            }
                            
                            const data = await retryResponse.json();
                            setWebSearchResults(prev => ({ ...prev, [msg.id]: data }));
                          } else if (!response.ok) {
                            // Clear stage timeouts on error
                            stageTimeouts.forEach(timeout => clearTimeout(timeout));
                            throw new Error('Web search failed');
                          } else {
                            // Clear stage timeouts when response is ready
                            stageTimeouts.forEach(timeout => clearTimeout(timeout));
                            
                            const data = await response.json();
                            setWebSearchResults(prev => ({ ...prev, [msg.id]: data }));
                          }
                        } catch (err) {
                          console.error('Web search error:', err);
                          setWebSearchResults(prev => ({
                            ...prev,
                            [msg.id]: { error: err.message || 'Web search failed' }
                          }));
                        } finally {
                          setWebSearchLoading(prev => ({ ...prev, [msg.id]: false }));
                          setWebSearchStage(prev => ({ ...prev, [msg.id]: '' }));
                        }
                      }}
                      disabled={webSearchLoading[msg.id]}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        backgroundColor: webSearchLoading[msg.id] ? '#ccc' : '#0066cc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: webSearchLoading[msg.id] ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!webSearchLoading[msg.id]) {
                          e.target.style.backgroundColor = '#0052a3';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!webSearchLoading[msg.id]) {
                          e.target.style.backgroundColor = '#0066cc';
                        }
                      }}
                    >
                      {webSearchLoading[msg.id] ? (
                        <>
                          <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 0.8s linear infinite' }} />
                          <span>
                            {webSearchStage[msg.id] === 'searching' ? 'Searching web...' :
                             webSearchStage[msg.id] === 'fetching' ? 'Fetching answers...' :
                             webSearchStage[msg.id] === 'analysing' ? 'Analysing answers...' :
                             webSearchStage[msg.id] === 'comparing' ? 'Comparing to internal sources...' :
                             webSearchStage[msg.id] === 'generating' ? 'Generating response...' :
                             'Searching web...'}
                          </span>
                        </>
                      ) : (
                        <>
                          <Globe style={{ width: '14px', height: '14px' }} />
                          <span>Search Web</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
                
                {/* Display web search results */}
                {msg.role === 'assistant' && webSearchResults[msg.id] && (
                  <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    {webSearchResults[msg.id].error ? (
                      <div style={{ color: '#d32f2f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertCircle style={{ width: '16px', height: '16px' }} />
                        <span>{webSearchResults[msg.id].error}</span>
                      </div>
                    ) : (
                      <>
                        {/* Internal Search Results */}
                        <div style={{ marginBottom: '16px' }}>
                          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
                            Internal Knowledge Base Results:
                          </h4>
                          <div style={{
                            padding: '12px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#666',
                            lineHeight: '1.6'
                          }}>
                            {webSearchResults[msg.id].internal_summary || msg.text}
                          </div>
                        </div>
                        
                        {/* Web Search Results */}
                        {webSearchResults[msg.id].web_results && (
                          <div style={{ marginBottom: '16px' }}>
                            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
                              Web Search Results:
                            </h4>
                            <div style={{
                              padding: '12px',
                              backgroundColor: 'white',
                              borderRadius: '4px',
                              fontSize: '13px',
                              color: '#666',
                              lineHeight: '1.6'
                            }}>
                              {webSearchResults[msg.id].web_results.map((result, idx) => (
                                <div key={idx} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: idx < webSearchResults[msg.id].web_results.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                                  <a
                                    href={result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: '#0066cc',
                                      textDecoration: 'none',
                                      fontWeight: '500',
                                      fontSize: '14px',
                                      display: 'block',
                                      marginBottom: '4px'
                                    }}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                  >
                                    {result.title}
                                  </a>
                                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#999' }}>
                                    {result.url}
                                  </p>
                                  {result.snippet && (
                                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                                      {result.snippet}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Combined Response */}
                        {webSearchResults[msg.id].combined_answer && (
                          <div>
                            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
                              Combined Answer:
                            </h4>
                            <div style={{
                              padding: '12px',
                              backgroundColor: '#e6f7e6',
                              borderRadius: '4px',
                              fontSize: '13px',
                              color: '#333',
                              lineHeight: '1.6',
                              border: '1px solid #b3e6b3'
                            }}>
                              {webSearchResults[msg.id].combined_answer}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                
                <div className="message-time">{formatTime(msg.timestamp)}</div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="message assistant">
              <div className="message-content">
                <div className="loading-message">
                  <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 0.8s linear infinite' }} />
                  <span>
                    {loadingStage === 'thinking' 
                      ? 'Thinking...' 
                      : loadingStage === 'searching'
                      ? 'Searching internal documents...' 
                      : loadingStage === 'compiling'
                      ? 'Compiling answer...'
                      : 'Processing...'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="message assistant">
              <div className="message-content error-content">
                <AlertCircle style={{ width: '16px', height: '16px' }} />
                <span>{error}</span>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        <form className="chat-input-form" onSubmit={handleSubmit}>
          <div className="chat-input-wrapper">
            <textarea
              ref={textareaRef}
              value={question}
              onChange={(e) => {
                const newValue = e.target.value;
                setQuestion(newValue);
                
                // Debounce resize to prevent flicker
                if (textareaRef.current) {
                  // Use a small timeout to batch resize operations
                  clearTimeout(textareaRef.current._resizeTimeout);
                  textareaRef.current._resizeTimeout = setTimeout(() => {
                    if (textareaRef.current) {
                      // Store current scroll position
                      const scrollTop = textareaRef.current.scrollTop;
                      
                      // Reset height to auto to get accurate scrollHeight
                      textareaRef.current.style.height = 'auto';
                      
                      // Calculate new height
                      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
                      textareaRef.current.style.height = `${newHeight}px`;
                      
                      // Restore scroll position
                      textareaRef.current.scrollTop = scrollTop;
                    }
                  }, 10);
                }
              }}
              placeholder="Ask a question about technical drawings, procedures, or documentation..."
              disabled={loading}
              rows={1}
              style={{
                resize: 'none',
                overflow: 'hidden',
                minHeight: '40px',
                maxHeight: '120px',
                lineHeight: '1.5',
                transition: 'none',
                boxSizing: 'border-box'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (question.trim() && !loading) {
                    handleSubmit(e);
                  }
                }
              }}
            />
            <button type="submit" className="chat-send-btn" disabled={loading || !question.trim()}>
              {loading ? (
                <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 0.8s linear infinite' }} />
              ) : (
                <Send style={{ width: '16px', height: '16px' }} />
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        documentUrl={documentPreview.documentUrl}
        fileName={documentPreview.fileName}
        page={documentPreview.page}
        isOpen={documentPreview.isOpen}
        onClose={() => setDocumentPreview({ isOpen: false, documentUrl: null, fileName: null, page: null })}
        getToken={getToken}
      />

      <div className="sources-panel visible">
        <div className="sources-header">
            {(() => {
              const hasDiagramSources = currentSources && Array.isArray(currentSources) && currentSources.some(s => s && s.document_type === 'diagram');
              return (
                <h2 className="sources-title">
                  {hasDiagramSources ? 'Referenced Drawings' : 'Sources & Evidence'}
                </h2>
              );
            })()}
        </div>
        <div className="sources-content">
          {!currentSources || !Array.isArray(currentSources) || currentSources.length === 0 ? (
            <div className="sources-empty">
              <FileText style={{ width: '24px', height: '24px', opacity: 0.3 }} />
              <p>Sources will appear here when referenced</p>
            </div>
          ) : (
            <>
              {(() => {
                const hasDiagramSources = currentSources && Array.isArray(currentSources) && currentSources.some(s => s && s.document_type === 'diagram');
                if (hasDiagramSources) {
                  return (
                    <div className="diagram-notice" style={{
                      padding: '12px',
                      marginBottom: '16px',
                      backgroundColor: '#f0f7ff',
                      border: '1px solid #b3d9ff',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#0066cc',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      <Info style={{ width: '16px', height: '16px', flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        This answer references engineering drawings. Drawings define specifications and geometry, not functional descriptions.
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              <div className="sources-list">
                {currentSources && Array.isArray(currentSources) && currentSources
                  .map((source, idx) => {
                  const isDiagram = source.document_type === 'diagram';
                  return (
                    <div 
                      key={idx} 
                      className="source-card"
                      style={{ animationDelay: `${idx * 100}ms` }}
                    >
                      <div className="source-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <FileText style={{ width: '16px', height: '16px' }} />
                        <span className="source-name">{source.file_name}</span>
                        {isDiagram && (
                          <span className="diagram-badge" style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            backgroundColor: '#e6f3ff',
                            color: '#0066cc',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500',
                            border: '1px solid #b3d9ff'
                          }}>
                            🧩 Technical Diagram
                          </span>
                        )}
                      </div>
                  {source.image_url && (
                    <SourceImage imageUrl={source.image_url} fileName={source.file_name} getToken={getToken} />
                  )}
                  {source.text && (
                    <div className="source-excerpt">{source.text}</div>
                  )}
                  {(source.page || source.section) && (
                    <div className="source-meta">
                      {source.section && <span>{source.section}</span>}
                      {source.page && <span>Page {source.page}</span>}
                    </div>
                  )}
                  {/* View Document buttons - show for any source with document_id */}
                  {(() => {
                    // Show buttons if source has document_id (works for all file types)
                    const hasDocumentAccess = source.document_id || source.pdf_url;
                    
                    if (!hasDocumentAccess) return null;
                    
                    // Determine file type from file name
                    const getFileType = (filename) => {
                      if (!filename || typeof filename !== 'string') return 'unknown';
                      const ext = filename.split('.').pop()?.toLowerCase();
                      return ext || 'unknown';
                    };
                    
                    const fileType = getFileType(source.file_name || '');
                    const isPDF = fileType === 'pdf';
                    const isOfficeDoc = ['docx', 'pptx', 'doc', 'ppt'].includes(fileType);
                    const isDiagram = source.document_type === 'diagram';
                    
                    // Determine button text based on file type
                    const getPreviewButtonText = () => {
                      if (source.is_top_diagram) {
                        return 'Preview Primary Diagram';
                      }
                      if (isDiagram) {
                        return 'Preview Diagram';
                      }
                      if (isPDF) {
                        return 'Preview Document';
                      }
                      if (isOfficeDoc) {
                        return 'Preview Document';
                      }
                      return 'Preview Document';
                    };
                    
                    return (
                    <div className="source-pdf-link" style={{
                      marginTop: '8px',
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}>
                      {/* Preview Button - opens modal */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          // Use pdf_url if available (now supports all file types), otherwise construct from document_id
                          const documentUrl = source.pdf_url || (source.document_id ? `/api/documents/${source.document_id}/file` : null);
                          if (!documentUrl) {
                            console.error('No document URL available');
                            return;
                          }
                          setDocumentPreview({
                            isOpen: true,
                            documentUrl: documentUrl,
                            fileName: source.file_name,
                            page: source.page
                          });
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: source.is_top_diagram ? '10px 14px' : '8px 12px',
                          backgroundColor: source.is_top_diagram ? '#0052a3' : '#0066cc',
                          color: 'white',
                          border: source.is_top_diagram ? '2px solid #003d7a' : 'none',
                          borderRadius: '4px',
                          fontSize: source.is_top_diagram ? '14px' : '13px',
                          fontWeight: source.is_top_diagram ? '600' : '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          boxShadow: source.is_top_diagram ? '0 2px 4px rgba(0,0,0,0.2)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = source.is_top_diagram ? '#003d7a' : '#0052a3';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = source.is_top_diagram ? '#0052a3' : '#0066cc';
                        }}
                      >
                        <Eye style={{ width: '14px', height: '14px' }} />
                        <span>{getPreviewButtonText()}</span>
                        {source.page && <span style={{ opacity: 0.9 }}>(Page {source.page})</span>}
                      </button>
                      
                      {/* Open in New Tab Button */}
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const token = await getToken();
                            if (!token) {
                              console.error('No auth token available');
                              return;
                            }
                            
                            // Use pdf_url if available (now supports all file types), otherwise construct from document_id
                            const documentUrl = source.pdf_url || (source.document_id ? `/api/documents/${source.document_id}/file` : null);
                            if (!documentUrl) {
                              console.error('No document URL available');
                              return;
                            }
                            
                            const fullUrl = documentUrl.startsWith('http') 
                              ? documentUrl 
                              : `${API_URL}${documentUrl}`;
                            
                            // Fetch document with auth and open in new tab
                            const response = await fetch(fullUrl, {
                              headers: {
                                'Authorization': `Bearer ${token}`,
                              },
                            });
                            
                            if (response.ok) {
                              const blob = await response.blob();
                              const blobUrl = URL.createObjectURL(blob);
                              const newWindow = window.open(blobUrl, '_blank');
                              
                              // Clean up blob URL after a delay
                              setTimeout(() => {
                                if (newWindow) {
                                  newWindow.onload = () => {
                                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                  };
                                } else {
                                  URL.revokeObjectURL(blobUrl);
                                }
                              }, 100);
                            } else {
                              console.error('Failed to fetch document:', response.statusText);
                            }
                          } catch (err) {
                            console.error('Failed to open document:', err);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 12px',
                          backgroundColor: 'transparent',
                          color: '#0066cc',
                          border: '1px solid #0066cc',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f0f7ff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        <FileText style={{ width: '14px', height: '14px' }} />
                        <span>Open in New Tab</span>
                      </button>
                    </div>
                    );
                  })()}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Ask;
